// --- Defaults (matching game-settings.component.ts editor defaults) ---
const DEFAULT_TITLE_OPT = { isActive: false, color: '#c4bfbf', showBackground: false, position: 'bottom' };
const DEFAULT_CATEGORY_OPT = { isActive: false, color: '#c4bfbf', showBackground: false, position: 'bottom' };
const DEFAULT_SHADOW_OPT = { isActive: false, color: 'rgba(0,0,0,0.75)', blur: false };

// --- Safe getters (defensive read, never mutate) ---

export function getEffectiveTitleOpt(game: any) {
  const opt = game && (game.titleOpt || (game.gameMenuArt && game.gameMenuArt.titleOpt))
    ? (game.titleOpt || game.gameMenuArt.titleOpt)
    : DEFAULT_TITLE_OPT;
  return {
    isActive: opt.isActive !== undefined ? opt.isActive : false,
    color: opt.color || DEFAULT_TITLE_OPT.color,
    showBackground: opt.showBackground !== undefined ? !!opt.showBackground : (opt.position === 'top'),
    position: opt.position || 'bottom',
  };
}

export function getEffectiveCategoryOpt(game: any) {
  const opt = game && (game.categoryOpt || (game.gameMenuArt && game.gameMenuArt.categoryOpt))
    ? (game.categoryOpt || game.gameMenuArt.categoryOpt)
    : DEFAULT_CATEGORY_OPT;
  return {
    isActive: opt.isActive !== undefined ? opt.isActive : false,
    color: opt.color || DEFAULT_CATEGORY_OPT.color,
    showBackground: opt.showBackground !== undefined ? !!opt.showBackground : (opt.position === 'top'),
    position: opt.position || 'bottom',
  };
}

export function getEffectiveShadowOpt(game: any) {
  const opt = game && (game.shadowOpt || (game.gameMenuArt && game.gameMenuArt.shadowOpt))
    ? (game.shadowOpt || game.gameMenuArt.shadowOpt)
    : DEFAULT_SHADOW_OPT;
  return {
    isActive: opt.isActive !== undefined ? opt.isActive : false,
    color: opt.color || DEFAULT_SHADOW_OPT.color,
    blur: opt.blur !== undefined ? opt.blur : false,
  };
}

// --- Positioning (percentage-based, works for any card size) ---

export function isSameEdge(game: any): boolean {
  const titleOpt = getEffectiveTitleOpt(game);
  const categoryOpt = getEffectiveCategoryOpt(game);

  return titleOpt.position === categoryOpt.position
         && titleOpt.isActive
         && categoryOpt.isActive
         && !!(game && game.category && game.category.name);
}

export function getSameEdgeContainerStyle(game: any, position: 'top' | 'bottom'): { [key: string]: string } {
  return {
    position: 'absolute',
    left: '0',
    right: '0',
    display: 'flex',
    'flex-direction': 'column',
    'align-items': 'center',
    'justify-content': 'center',
    top: position === 'top' ? '13%' : 'auto',
    bottom: position === 'bottom' ? '10%' : 'auto'
  };
}

export function getTitlePositionStyle(game: any): { [key: string]: string } {
  const titleOpt = getEffectiveTitleOpt(game);

  if (titleOpt.position === 'top') {
    return { top: '13%', bottom: 'auto' };
  } else {
    return { top: 'auto', bottom: '10%' };
  }
}

export function getCategoryPositionStyle(game: any): { [key: string]: string } {
  const categoryOpt = getEffectiveCategoryOpt(game);

  if (categoryOpt.position === 'top') {
    return { top: '13%', bottom: 'auto' };
  } else {
    return { top: 'auto', bottom: '10%' };
  }
}

// --- Text shadow computation (deduplicated) ---

export function getTextShadow(game: any): string {
  const shadow = getEffectiveShadowOpt(game);
  if (!shadow.isActive) return 'none';

  return shadow.blur
    ? '0 0 10px ' + shadow.color
    : '-1px 1px 10px ' + shadow.color;
}

// --- Title text style (font size, color, truncation — deduplicated) ---

export function getTitleTextStyle(game: any): { [key: string]: string } {
  const titleOpt = getEffectiveTitleOpt(game);

  if (!titleOpt.isActive) {
    return { display: 'none' };
  }

  const title = game && game.title ? game.title : '';
  const titleLength = title.length;
  const fontSize =
    titleLength <= 20 ? '16px' :
    titleLength <= 30 ? '14px' :
    titleLength <= 50 ? '12px' : '11px';

  return {
    color: titleOpt.color,
    'text-shadow': getTextShadow(game),
    'font-size': fontSize,
    display: '-webkit-box',
    overflow: 'hidden',
    'text-overflow': 'ellipsis',
    'word-break': 'break-word',
    'white-space': 'normal',
    'line-height': '1.2',
    'max-height': '2.4em',
    '-webkit-box-orient': 'vertical',
    '-webkit-line-clamp': '2',
    padding: '0 0.4rem',
    'max-width': '100%',
    'font-family': "'AvenirNextCondensedBold', sans-serif",
  };
}

// --- Category badge style (deduplicated) ---

export function getCategoryTextStyle(game: any): { [key: string]: string } {
  const categoryOpt = getEffectiveCategoryOpt(game);

  if (!categoryOpt.isActive || !(game && game.category && game.category.name)) {
    return { display: 'none' };
  }

  return {
    color: categoryOpt.color,
    'text-shadow': getTextShadow(game),
    'font-size': '14px',
    'font-weight': '500',
    padding: '0.25rem 0.5rem',
    'border-radius': '4px',
  };
}
